/**
 * Payments and allocations.
 *
 * WHY ALLOCATIONS ARE ROWS
 * -----------------------
 * A `paidAmount` column on the invoice cannot represent one receipt of
 * Rs 1,00,000 settling three invoices of 40,000 / 35,000 / 25,000. 1.6.x
 * had exactly that column, which is why its receivables report had to guess
 * and why a payment could be "recorded" without ever being tied to the
 * document it settled.
 *
 * With allocations as rows, the customer ledger and the ageing report become
 * a join rather than an inference.
 *
 * CANCELLING
 * ----------
 * A document with money allocated to it cannot be cancelled. Voiding the
 * payment silently would leave the party ledger disagreeing with the bank,
 * so the service refuses and the user reverses explicitly.
 */
import type { Table } from 'dexie';
import type { DocType } from '@domain/docTypes';
import { canCancel, derivePaymentStatus } from '@domain/lifecycle';
import { ulid } from '../../platform/ids';
import type {
  AuditLog,
  Document,
  DocumentSequence,
  PartySnapshot,
  Payment,
  SyncOutboxEntry,
  PaymentAllocation,
} from '../db/types';
import { allocateDocNumber } from '../repositories/sequences';
import { recordBusinessMutations, nextVersion } from '../sync/recordBusinessMutation';

export type PaymentErrorCode =
  | 'OVER_ALLOCATION'
  | 'UNKNOWN_DOCUMENT'
  | 'WRONG_PARTY'
  | 'DOCUMENT_CANCELLED'
  | 'DOCUMENT_HAS_PAYMENTS';

export class PaymentServiceError extends Error {
  readonly code: PaymentErrorCode;
  constructor(code: PaymentErrorCode, message: string) {
    super(message);
    this.name = 'PaymentServiceError';
    this.code = code;
  }
}

export interface PaymentTx {
  payments: Table<Payment, string>;
  paymentAllocations: Table<PaymentAllocation, string>;
  documents: Table<Document, string>;
  documentSequences: Table<DocumentSequence, string>;
  auditLogs: Table<AuditLog, string>;
  /**
   * The sync outbox, in the SAME transaction.
   *
   * Required, not optional. A payment that exists locally but never reaches the
   * outbox is a payment the user believes is saved and no other device will
   * ever see — a silent divergence, and the single most damaging thing sync can
   * do to a ledger. Making it part of the table bag means a caller that forgets
   * it fails loudly at the Dexie scope check instead of quietly skipping sync.
   */
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

export interface RecordPaymentParams {
  businessId: string;
  direction: 'RECEIPT' | 'PAYMENT';
  customerId?: string;
  supplierId?: string;
  partySnapshot: PartySnapshot;
  paymentDate: string;
  amountPaise: number;
  method: Payment['method'];
  reference?: string;
  bankAccountId?: string;
  narration?: string;
  user: { id: string; name: string };
  now: string;
  /**
   * This install's device id, so the mutation identifies where it came from.
   *
   * Passed in rather than looked up here: reading it would need the `settings`
   * table in the transaction scope, and widening every payment transaction to
   * carry a table it does not use would be a worse trade than one parameter.
   * The caller resolves it once with `currentDeviceId(db)`.
   */
  deviceId: string;
}

export interface AllocationInput {
  documentId: string;
  amountPaise: number;
}

export interface RecordPaymentResult {
  id: string;
  paymentNumber: string;
  allocatedPaise: number;
  unallocatedPaise: number;
  documentsTouched: string[];
}

/**
 * The value a document has been CREDITED back by.
 *
 * WHY THIS IS DERIVED AND NOT STORED ON THE INVOICE
 * -------------------------------------------------
 * The obvious implementation is to subtract the credit from the invoice's
 * `balancePaise` when the credit note is issued. That is wrong, and wrong in a way
 * that is invisible: `balancePaise` is a DERIVED field, recomputed from
 * `grandTotalPaise - allocatedPaise` every time a payment is recorded or an
 * allocation is voided. A one-off subtraction would therefore be silently
 * discarded by the very next receipt — the customer would owe the full amount
 * again, with no error anywhere.
 *
 * So the credit is recomputed FROM ITS OWN DOCUMENTS, exactly the way allocated
 * money is recomputed from allocation rows. The credit note is the source of
 * truth, and the invoice's balance is a function of it. Nothing is silently
 * rewritten, and nothing is lost.
 *
 * DRAFT credits are excluded: an unissued credit note has not reduced anything,
 * which is the same rule that keeps a draft from moving stock.
 */
export async function sumIssuedCredits(
  tx: Pick<PaymentTx, 'documents'>,
  documentId: string,
): Promise<number> {
  const rows = await tx.documents.where('originalDocId').equals(documentId).toArray();
  return rows
    .filter(
      (doc) =>
        (doc.docType === 'CREDIT_NOTE' || doc.docType === 'SALES_RETURN') &&
        doc.status === 'ISSUED',
    )
    .reduce((sum, doc) => sum + doc.grandTotalPaise, 0);
}

/**
 * What a document still owes, after both money received and value credited back.
 *
 * Clamped at zero: a credit may exceed what was outstanding (a customer who paid
 * in full and then returned goods is owed money, not negative money), and a
 * negative balance would make every later receipt arithmetic wrong.
 */
export function outstandingPaise(
  grandTotalPaise: number,
  allocatedPaise: number,
  creditedPaise: number,
): number {
  return Math.max(0, grandTotalPaise - allocatedPaise - creditedPaise);
}

/** Allocations on a document that have not been voided. */
export async function sumLiveAllocations(
  tx: Pick<PaymentTx, 'paymentAllocations'>,
  documentId: string,
): Promise<number> {
  const rows = await tx.paymentAllocations.where('documentId').equals(documentId).toArray();
  return rows.filter((r) => !r.voidedAt).reduce((s, r) => s + r.amountPaise, 0);
}

/**
 * Record a payment and allocate it, in one transaction.
 *
 * Allocation is optional: an unallocated remainder is an advance or an
 * on-account balance, which is a normal and useful thing to record.
 */
export async function recordPayment(
  tx: PaymentTx,
  params: RecordPaymentParams,
  allocations: readonly AllocationInput[] = [],
): Promise<RecordPaymentResult> {
  if (params.direction === 'RECEIPT' ? !params.customerId : !params.supplierId) {
    throw new PaymentServiceError('WRONG_PARTY', 'Select the customer or supplier for this payment.');
  }
  if (!Number.isInteger(params.amountPaise) || params.amountPaise <= 0) {
    throw new PaymentServiceError(
      'OVER_ALLOCATION',
      'Payment amount must be a positive whole number of paise.',
    );
  }

  const requested = allocations.reduce((s, a) => s + a.amountPaise, 0);
  if (allocations.some((a) => !Number.isInteger(a.amountPaise) || a.amountPaise <= 0)) {
    throw new PaymentServiceError(
      'OVER_ALLOCATION',
      'Each allocation must be a positive whole number of paise.',
    );
  }
  if (requested > params.amountPaise) {
    throw new PaymentServiceError(
      'OVER_ALLOCATION',
      `Allocations total Rs ${requested / 100} but the payment is Rs ${params.amountPaise / 100}.`,
    );
  }

  const number = await allocateDocNumber(tx as never, {
    businessId: params.businessId,
    docType: params.direction === 'RECEIPT' ? 'SALES_INVOICE' : 'PURCHASE_INVOICE',
    docDate: params.paymentDate,
    issuedBy: params.user.id,
    issuedAt: params.now,
    format: { prefix: params.direction === 'RECEIPT' ? 'RCP' : 'PAY' },
  });

  const paymentId = ulid();
  const allocatedPaise = requested;

  const payment: Payment = {
    id: paymentId,
    businessId: params.businessId,
    paymentNumber: number.docNumber,
    direction: params.direction,
    customerId: params.customerId,
    supplierId: params.supplierId,
    partySnapshot: params.partySnapshot,
    paymentDate: params.paymentDate,
    amountPaise: params.amountPaise,
    allocatedPaise,
    unallocatedPaise: params.amountPaise - allocatedPaise,
    method: params.method,
    reference: params.reference,
    bankAccountId: params.bankAccountId,
    narration: params.narration,
    expenseIds: [],
    status: 'POSTED',
    createdAt: params.now,
    createdBy: params.user.id,
    updatedAt: params.now,
  };
  await tx.payments.add(payment);

  const touched: string[] = [];
  const rows: PaymentAllocation[] = [];


  for (const a of allocations) {
    const doc = await tx.documents.get(a.documentId);
    if (!doc) {
      throw new PaymentServiceError('UNKNOWN_DOCUMENT', `Document ${a.documentId} does not exist.`);
    }
    if (doc.status === 'CANCELLED') {
      throw new PaymentServiceError('DOCUMENT_CANCELLED', `${doc.docNumber} is cancelled.`);
    }

    if (doc.docType !== (params.direction === 'RECEIPT' ? 'SALES_INVOICE' : 'PURCHASE_INVOICE')) {
      throw new PaymentServiceError('WRONG_PARTY', `${doc.docNumber} cannot receive this payment type.`);
    }
    if (doc.status !== 'ISSUED' && doc.status !== 'PARTIALLY_SETTLED') {
    throw new PaymentServiceError(
      'DOCUMENT_CANCELLED',
      `${doc.docNumber} must be issued before it can receive a payment.`,
    );
  }
    if (doc.businessId !== params.businessId) {
      throw new PaymentServiceError('WRONG_PARTY', `${doc.docNumber} belongs to another business.`);
    }

    const isReceipt = params.direction === 'RECEIPT';
    const partyOnDoc = isReceipt ? doc.customerId : doc.supplierId;
    const myParty = isReceipt ? params.customerId : params.supplierId;
    if (partyOnDoc !== myParty) {
      throw new PaymentServiceError(
        'WRONG_PARTY',
        `${doc.docNumber} is not owed to / payable by this party.`,
      );
    }

    if (allocations.filter((allocation) => allocation.documentId === doc.id).length > 1) {
      throw new PaymentServiceError(
        'OVER_ALLOCATION',
        `Invoice ${doc.docNumber} can only appear once in a payment allocation.`,
      );
    }

    const priorInBatch = rows
      .filter((row) => row.documentId === doc.id)
      .reduce((sum, row) => sum + row.amountPaise, 0);
    if (a.amountPaise + priorInBatch > doc.balancePaise) {
      throw new PaymentServiceError(
        'OVER_ALLOCATION',
        `Allocating Rs ${a.amountPaise / 100} to ${doc.docNumber}, ` +
          `which has Rs ${doc.balancePaise / 100} outstanding.`,
      );
    }

    rows.push({
      id: ulid(),
      businessId: params.businessId,
      paymentId,
      documentId: doc.id,
      documentType: doc.docType as DocType,
      documentNumber: doc.docNumber,
      customerId: doc.customerId,
      supplierId: doc.supplierId,
      amountPaise: a.amountPaise,
      createdAt: params.now,
      createdBy: params.user.id,
    });
    touched.push(doc.id);
  }

  if (rows.length) await tx.paymentAllocations.bulkAdd(rows);

  // Recompute each document's rollup FROM the allocation rows, never by
  // incrementing. An increment drifts the moment an allocation is voided.
  //
  // The balance subtracts ISSUED CREDITS as well as allocations. Omitting the
  // credit term is not a simplification: it would resurrect money the customer
  // already had credited back the moment they next paid, and the receipt would
  // silently re-create the debt the credit note was raised to remove.
  const updatedDocuments: Document[] = [];
  for (const documentId of touched) {
    const doc = await tx.documents.get(documentId);
    if (!doc) continue;
    const sum = await sumLiveAllocations(tx, documentId);
    const credited = await sumIssuedCredits(tx, documentId);
    const effectiveTotal = Math.max(0, doc.grandTotalPaise - credited);
    const status = derivePaymentStatus(effectiveTotal, sum);
     const version = nextVersion(doc.syncVersion);
    const updated = {
      ...doc,
      allocatedPaise: sum,
      balancePaise: outstandingPaise(doc.grandTotalPaise, sum, credited),
      status,
      updatedAt: params.now,
      updatedBy: params.user.id,
      syncVersion: version,
    };
    await tx.documents.put(updated);
    updatedDocuments.push(updated);
  }

  await tx.auditLogs.add({
    id: ulid(),
    businessId: params.businessId,
    entityType: 'PAYMENT',
    entityId: paymentId,
    action: 'PAYMENT',
    after: JSON.stringify({ paymentNumber: payment.paymentNumber, amountPaise: payment.amountPaise }),
    amountPaise: payment.amountPaise,
    userId: params.user.id,
    userName: params.user.name,
    occurredAt: params.now,
  });

  // ── SYNC ──────────────────────────────────────────────────────────────────
  // Inside this transaction, after every write above has succeeded.
  //
  // ONE mutation for the payment and ONE for the whole allocation set, not one
  // per allocation. Allocations are a set belonging to the payment; splitting
  // them across mutations would let a retry deliver half of them, and a payment
  // whose allocations are half-present is worse than one that is absent.
  //
  // A RETRIED recordPayment MUST NOT DOUBLE-CHARGE. The ids come from the
  // payment and allocation rows themselves, so re-publishing the same logical
  // payment carries the same `changeId` and the provider's dedup makes it a
  // no-op. That is the whole reason a payment is a row with a stable id rather
  // than a value with a timestamp.
  const paymentVersion = nextVersion(payment.syncVersion);
  const postedPayment = { ...payment, syncVersion: paymentVersion };
  await tx.payments.update(paymentId, { syncVersion: paymentVersion });
  await recordBusinessMutations(tx, [
    ...updatedDocuments.map((doc) => ({
      businessId: doc.businessId,
      deviceId: params.deviceId,
      entityType: 'document' as const,
      entityId: doc.id,
      operation: 'UPSERT' as const,
      version: doc.syncVersion ?? 1,
      changedAt: params.now,
      payload: doc as unknown as Record<string, unknown>,
    })),
    {
      businessId: payment.businessId,
      deviceId: params.deviceId,
      entityType: 'payment',
      entityId: payment.id,
      operation: 'UPSERT',
      version: paymentVersion,
      changedAt: params.now,
      // Minted by the recorder, per mutation.
      //
      // It was derived from the payment id alone, which the provider collapsed
      // whenever the same payment was written again, and then from the payment id
      // plus the version, which collided whenever two devices recorded against the
      // same base version. A payment that never reaches the other device is a
      // reconciliation failure, not a sync nuisance.
      payload: postedPayment as unknown as Record<string, unknown>,
    },
    ...rows.map((allocation) => {
      // Computed ONCE. It was called twice for the same row, which happened to
      // agree only because the row is new and the counter is a pure function.
      const allocationVersion = nextVersion(allocation.syncVersion);
      return {
        businessId: allocation.businessId,
        deviceId: params.deviceId,
        entityType: 'paymentAllocation' as const,
        entityId: allocation.id,
        operation: 'UPSERT' as const,
        version: allocationVersion,
        changedAt: params.now,
        payload: { ...allocation, syncVersion: allocationVersion },
      };
    }),
  ]);

  return {
    id: paymentId,
    paymentNumber: payment.paymentNumber,
    allocatedPaise,
    unallocatedPaise: payment.unallocatedPaise,
    documentsTouched: touched,
  };
}

/**
 * Reverse a document's allocations.
 *
 * The rows are marked voided, never deleted: the record that money once
 * arrived against this invoice is part of the audit trail, and a deletion
 * would make a disputed balance unreconstructable.
 */
export async function voidAllocationsForDocument(
  tx: PaymentTx,
  documentId: string,
  reason: string,
  user: { id: string; name: string },
  now: string,
  deviceId: string,
): Promise<number> {
  const rows = await tx.paymentAllocations.where('documentId').equals(documentId).toArray();
  const live = rows.filter((r) => !r.voidedAt);
  if (!live.length) return 0;

  const allocationMutations = live.map((allocation) => {
    const version = nextVersion(allocation.syncVersion);
    const updated = { ...allocation, syncVersion: version, voidedAt: now };
    return {
      updated,
      mutation: {
        businessId: allocation.businessId,
        deviceId,
        entityType: 'paymentAllocation' as const,
        entityId: allocation.id,
        operation: 'UPSERT' as const,
        version,
        changedAt: now,
        payload: updated as unknown as Record<string, unknown>,
      },
    };
  });
  await tx.paymentAllocations.bulkPut(allocationMutations.map(({ updated }) => updated));
  await recordBusinessMutations(tx, allocationMutations.map(({ mutation }) => mutation));

  // Roll the payment's own totals back by exactly what was freed.
  const paymentMutations: Parameters<typeof recordBusinessMutations>[1][number][] = [];
  for (const paymentId of new Set(live.map((r) => r.paymentId))) {
    const payment = await tx.payments.get(paymentId);
    if (!payment) continue;
    const freed = live
      .filter((r) => r.paymentId === paymentId)
      .reduce((s, r) => s + r.amountPaise, 0);
    const allocatedPaise = payment.allocatedPaise - freed;
    const version = nextVersion(payment.syncVersion);
    const updated = {
      ...payment,
      allocatedPaise,
      unallocatedPaise: payment.amountPaise - allocatedPaise,
      updatedAt: now,
      syncVersion: version,
    };
    await tx.payments.put(updated);
    paymentMutations.push({
      businessId: payment.businessId,
      deviceId,
      entityType: 'payment',
      entityId: payment.id,
      operation: 'UPSERT',
      version,
      changedAt: now,
      payload: updated as unknown as Record<string, unknown>,
    });
  }

  const doc = await tx.documents.get(documentId);
  if (doc) {
    const sum = await sumLiveAllocations(tx, documentId);
    // Same credit-aware formula as `recordPayment`. Two copies of a balance rule
    // is how a balance rule drifts, so both call the same helpers.
    const credited = await sumIssuedCredits(tx, documentId);
    const effectiveTotal = Math.max(0, doc.grandTotalPaise - credited);
    const updated = {
      ...doc,
      allocatedPaise: sum,
      balancePaise: outstandingPaise(doc.grandTotalPaise, sum, credited),
      status: derivePaymentStatus(effectiveTotal, sum),
      updatedAt: now,
      updatedBy: user.id,
      syncVersion: nextVersion(doc.syncVersion),
    };
    await tx.documents.put(updated);
    paymentMutations.push({
      businessId: doc.businessId,
      deviceId,
      entityType: 'document',
      entityId: doc.id,
      operation: 'UPSERT',
      version: updated.syncVersion ?? 1,
      changedAt: now,
      payload: updated as unknown as Record<string, unknown>,
    });
  }
  await recordBusinessMutations(tx, paymentMutations);

  await tx.auditLogs.add({
    id: ulid(),
    businessId: doc?.businessId,
    entityType: 'DOCUMENT',
    entityId: documentId,
    action: 'ALLOCATE',
    before: JSON.stringify({ allocatedPaise: doc?.allocatedPaise ?? 0 }),
    after: JSON.stringify({ voided: live.length, reason }),
    userId: user.id,
    userName: user.name,
    occurredAt: now,
    reason,
  });

  return live.length;
}

/**
 * Cancel a document, refusing while money is allocated to it.
 *
 * The reversal is explicit: the user voids the allocation first, then the
 * document can be cancelled. A single "cancel" that silently erased a
 * payment would leave the party ledger disagreeing with the bank.
 */
export async function cancelDocument(
  tx: PaymentTx,
  documentId: string,
  reason: string,
  user: { id: string; name: string },
  now: string,
  deviceId: string,
): Promise<{ voidedAllocations: number }> {
  const doc = await tx.documents.get(documentId);
  if (!doc) throw new PaymentServiceError('UNKNOWN_DOCUMENT', 'Document not found.');

  const allocated = await sumLiveAllocations(tx, documentId);
  const guard = canCancel({
    docType: doc.docType,
    status: doc.status,
    lineCount: 1,
    hasCounterparty: Boolean(doc.customerId ?? doc.supplierId),
    allocatedPaise: allocated,
    isReferenced: doc.linkedChildDocIds.length > 0,
  });
  if (!guard.ok) {
    throw new PaymentServiceError(
      guard.code === 'HAS_PAYMENTS' ? 'DOCUMENT_HAS_PAYMENTS' : 'DOCUMENT_CANCELLED',
      guard.message,
    );
  }
  if (doc.linkedChildDocIds.length > 0) {
    throw new PaymentServiceError('DOCUMENT_CANCELLED', 'This invoice is referenced by another document and cannot be cancelled here.');
  }

  const voidedAllocations = await voidAllocationsForDocument(tx, documentId, reason, user, now, deviceId);
  const cancelledVersion = nextVersion(doc.syncVersion);
  const cancelled = {
    ...doc,
    status: 'CANCELLED' as const,
    cancelledAt: now,
    cancelledBy: user.id,
    cancelReason: reason,
    updatedAt: now,
    updatedBy: user.id,
    syncVersion: cancelledVersion,
  };
  await tx.documents.put(cancelled);
  await recordBusinessMutations(tx, [{
    businessId: doc.businessId,
    deviceId,
    entityType: 'document',
    entityId: doc.id,
    operation: 'UPSERT',
    version: cancelledVersion,
    changedAt: now,
    payload: cancelled as unknown as Record<string, unknown>,
  }]);

  await tx.auditLogs.add({
    id: ulid(),
    businessId: doc.businessId,
    entityType: 'DOCUMENT',
    entityId: doc.id,
    action: 'CANCEL',
    before: JSON.stringify({ status: doc.status }),
    after: JSON.stringify({ status: 'CANCELLED', reason }),
    amountPaise: doc.grandTotalPaise,
    userId: user.id,
    userName: user.name,
    occurredAt: now,
    reason,
  });


  return { voidedAllocations };
}
